import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Search, Package, MapPin, Clock, Save, RotateCcw,
  CheckCircle, AlertCircle, Calendar, User, Navigation,
  Truck, Camera, FileText, DollarSign, HelpCircle
} from 'lucide-react';
import apiClient from '../services/api';

/* ── Debounce ── */
function useDebounce(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

/* ── TypeBadge ── */
const TypeBadge = ({ type }) => {
  const map = {
    LT: { bg: '#ddd6fe', color: '#5b21b6', label: 'Land Transport' },
    D:  { bg: '#d1fae5', color: '#065f46', label: 'Domestic' },
    I:  { bg: '#fef3c7', color: '#92400e', label: 'International' },
  };
  const s = map[type] || { bg: '#f3f4f6', color: '#374151', label: type };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[0.78rem] font-bold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}>{s.label}</span>
  );
};

/* ── highlightMatch ── */
function highlightMatch(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>{text.slice(0, idx)}<mark className="bg-yellow-200 text-gray-900 rounded px-px">{text.slice(idx, idx + query.length)}</mark>{text.slice(idx + query.length)}</>
  );
}

/* ── DropdownPortal ── */
const DropdownPortal = ({ anchorRef, children }) => {
  const [pos, setPos] = useState(null);
  const reposition = () => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom, left: r.left, width: r.width });
  };
  useEffect(() => {
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [anchorRef]); // eslint-disable-line
  if (!pos) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 999999 }}>{children}</div>,
    document.body
  );
};

/* ── ConsignmentSearchBox ── */
const ConsignmentSearchBox = ({ onSelect, disabled }) => {
  const [query,       setQuery]     = useState('');
  const [results,     setResults]   = useState([]);
  const [open,        setOpen]      = useState(false);
  const [searching,   setSearching] = useState(false);
  const [highlighted, setHL]        = useState(0);
  const [searchError, setSearchError] = useState('');
  const inputRef       = useRef(null);
  const wrapRef        = useRef(null);
  const listRef        = useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!debouncedQuery.trim()) { setResults([]); setOpen(false); setSearchError(''); return; }
    let cancelled = false;
    (async () => {
      setSearching(true); setSearchError('');
      try {
        const res = await apiClient.get(`/ConsignmentSearch.php?search=${encodeURIComponent(debouncedQuery)}`);
        if (cancelled) return;
        if (res.data.success && res.data.data?.length > 0) { setResults(res.data.data); setHL(0); setOpen(true); }
        else { setResults([]); setOpen(true); }
      } catch { if (!cancelled) { setSearchError('Search failed — check connection'); setOpen(false); } }
      finally { if (!cancelled) setSearching(false); }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  useEffect(() => {
    const handler = (e) => { if (!wrapRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (highlighted >= 0 && listRef.current) listRef.current.querySelectorAll('[data-item]')[highlighted]?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const selectItem  = (item) => { setQuery(item.ConsignmentNo); setOpen(false); setResults([]); onSelect(item); };
  const handleClear = () => { setQuery(''); setResults([]); setOpen(false); setSearchError(''); inputRef.current?.focus(); };
  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHL(h => Math.min(h + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHL(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (results[highlighted]) selectItem(results[highlighted]); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center"
          style={{ color: searching ? '#3b82f6' : '#9ca3af' }}>
          {searching
            ? <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            : <Search size={16} />}
        </div>
        <input ref={inputRef} type="text" value={query}
          onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Type consignment no., shipper or consignee…"
          disabled={disabled} autoFocus
          className={`w-full pl-10 pr-9 py-3 border rounded-lg text-sm bg-white outline-none transition-all duration-150 box-border ${open ? 'border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]' : 'border-gray-300'}`} />
        {query && (
          <button type="button" onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 bg-transparent border-0 cursor-pointer text-gray-400 hover:text-gray-600 flex items-center">
            <X size={15} />
          </button>
        )}
      </div>

      {open && (
        <DropdownPortal anchorRef={wrapRef}>
          <div ref={listRef} className="bg-white border-2 border-blue-500 rounded-xl shadow-2xl max-h-[360px] overflow-y-auto">
            {results.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <Package size={32} className="mx-auto mb-2.5 opacity-25" />
                <div className="text-sm font-semibold">No consignments found</div>
                <div className="text-xs mt-1 text-gray-400">No results for "{debouncedQuery}"</div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center px-3.5 py-2 bg-gray-50 border-b border-gray-200 rounded-t-xl text-[11px] font-bold tracking-widest text-gray-500">
                  <span>{results.length} RESULT{results.length !== 1 ? 'S' : ''}</span>
                  <span className="font-normal text-[11px]">↑↓ navigate · Enter select · Esc close</span>
                </div>
                {results.map((item, idx) => {
                  const isHL  = idx === highlighted;
                  const route = [item.OriginCity || item.OriginCountry, item.DestinationCity || item.DestinationCountry].filter(Boolean).join(' → ');
                  return (
                    <div key={item.ConsignmentID} data-item
                      onMouseEnter={() => setHL(idx)} onClick={() => selectItem(item)}
                      className={`grid gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors duration-75 ${isHL ? 'bg-blue-50' : 'bg-white'}`}
                      style={{ gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <TypeBadge type={item.Type} />
                          <span className={`font-mono font-bold text-[0.9rem] ${isHL ? 'text-blue-700' : 'text-gray-900'}`}>{highlightMatch(item.ConsignmentNo, query)}</span>
                          {item.ConsignmentDate && <span className="text-[0.76rem] text-gray-400">{new Date(item.ConsignmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                        </div>
                        <div className="flex gap-3.5 text-[0.81rem] text-gray-600 flex-wrap">
                          {(item.ShipperName || item.Shipper)     && <span className="flex items-center gap-0.5"><User size={11} className="opacity-50" />{highlightMatch(item.ShipperName || item.Shipper, query)}</span>}
                          {(item.ConsigneeName || item.Consignee) && <span className="text-gray-500">→ {highlightMatch(item.ConsigneeName || item.Consignee, query)}</span>}
                          {route && <span className="flex items-center gap-0.5 text-gray-400"><MapPin size={11} className="opacity-50" />{route}</span>}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        {item.Status && <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-[0.74rem] font-semibold whitespace-nowrap">{item.Status}</span>}
                        {(item.Weight || item.Pieces) && <span className="text-[0.76rem] text-gray-400 whitespace-nowrap">{item.Weight ? `${item.Weight} kg` : ''}{item.Pieces ? ` · ${item.Pieces} pcs` : ''}</span>}
                        {isHL && <span className="text-[0.68rem] text-blue-500 font-bold">Enter ↵</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DropdownPortal>
      )}

      {searchError && (
        <div className="flex items-center gap-1 mt-1.5 text-[0.82rem] text-red-500">
          <AlertCircle size={13} />{searchError}
        </div>
      )}
      <style>{`@keyframes cse-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* ── SelectedBar ── */
const SelectedBar = ({ data, onClear }) => (
  <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-blue-50 border-2 border-blue-200 rounded-lg flex-wrap">
    <Package size={15} className="flex-shrink-0 text-blue-600" />
    <span className="font-mono font-bold text-[0.92rem] text-blue-800">{data.ConsignmentNo}</span>
    <span className="text-[0.82rem] text-gray-700 flex-1 min-w-0 truncate">
      {[data.ShipperName || data.Shipper, data.ConsigneeName || data.Consignee].filter(Boolean).join(' → ')}
    </span>
    <button type="button" onClick={onClear}
      className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-300 rounded-md text-[0.8rem] font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 flex-shrink-0 transition-colors duration-100">
      <X size={12} /> Change
    </button>
  </div>
);

/* ── deliveryStatusCls ── */
const deliveryStatusCls = (val) =>
  val?.toLowerCase() === 'delivered'
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-amber-100 text-amber-800';

/* ══════════════════════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════════════════════ */
const ConsignmentStatusEntry = () => {
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState('');
  const [consignmentData, setConsignmentData] = useState(null);
  const [statusHistory,   setStatusHistory]   = useState([]);
  const [courierStatuses, setCourierStatuses] = useState([]);
  const [deliveryStatuses,setDeliveryStatuses]= useState([]);
  const [employees,       setEmployees]       = useState([]);
  const [sigPreview,      setSigPreview]      = useState(null);
  const [photoPreview,    setPhotoPreview]    = useState(null);
  const [showHelp,        setShowHelp]        = useState(false);

  const undeliveredReasons = ['No Response','Wrong Address','Customer Requested','Payment Issue','Weather/Traffic','Recipient Unavailable','Access Denied','Other'];

  const [form, setForm] = useState({
    StatusDate: new Date().toISOString().slice(0, 16),
    CourierStatusID: '', Location: '', Remarks: '',
    EstimatedDeliveryDate: '', EstimatedDeliveryTimeSlot: '',
    ActualDeliveryDate: '', ActualDeliveryTime: '',
    DeliveryOTP: '', DeliveredByID: '', DeliveredBy: '', ReceivedBy: '',
    DeliveryStatus: '', UndeliveredReason: '', NextAttemptDateTime: '',
    SpecialTimeDeliveryCharge: '', AfterHoursDeliveryCharge: '',
  });

  useEffect(() => {
    apiClient.get('/CourierStatus.php?active=1').then(r => { if (r.data.success) setCourierStatuses(r.data.data || []); }).catch(() => {});
    apiClient.get('/DeliveryStatus.php?active=1').then(r => { if (r.data.success) setDeliveryStatuses(r.data.data || []); }).catch(() => {});
    apiClient.get('/Employees.php', { params: { isactive: 1 } }).then(r => { if (r.data?.success) setEmployees(r.data.data || []); }).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'F5') { e.preventDefault(); if (consignmentData) handleSave(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [consignmentData, form]); // eslint-disable-line

  const handleSelect = (c) => {
    setConsignmentData(c);
    setForm(p => ({ ...p, Location: c.DestinationCity || c.DestinationCountry || '' }));
    setError(''); setSuccess('');
    apiClient.get(`/ConsignmentStatusHistory.php?consignmentid=${c.ConsignmentID}`)
      .then(r => setStatusHistory(r.data.success ? r.data.data || [] : [])).catch(() => setStatusHistory([]));
  };

  const handleChange = (e) => { const { name, value } = e.target; setForm(p => ({ ...p, [name]: value })); };

  const handleFile = (e, type) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { type === 'sig' ? setSigPreview(reader.result) : setPhotoPreview(reader.result); };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!consignmentData)      { setError('No consignment loaded'); return; }
    if (!form.CourierStatusID) { setError('Please select a status'); return; }
    if (!form.StatusDate)      { setError('Status date is required'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await apiClient.post('/ConsignmentStatusHistory.php', {
        ConsignmentID: consignmentData.ConsignmentID,
        CourierStatusID: form.CourierStatusID, StatusDate: form.StatusDate,
        Location: form.Location || null, Remarks: form.Remarks || null,
        EstimatedDeliveryDate: form.EstimatedDeliveryDate || null, EstimatedDeliveryTimeSlot: form.EstimatedDeliveryTimeSlot || null,
        ActualDeliveryDate: form.ActualDeliveryDate || null, ActualDeliveryTime: form.ActualDeliveryTime || null,
        DeliveryOTP: form.DeliveryOTP || null, DeliveredByID: form.DeliveredByID || null,
        DeliveredBy: form.DeliveredBy || null, ReceivedBy: form.ReceivedBy || null,
        DeliveryStatus: form.DeliveryStatus || null, UndeliveredReason: form.UndeliveredReason || null,
        NextAttemptDateTime: form.NextAttemptDateTime || null,
        SpecialTimeDeliveryCharge: form.SpecialTimeDeliveryCharge || null,
        AfterHoursDeliveryCharge: form.AfterHoursDeliveryCharge || null,
      });
      if (res.data.success) {
        setSuccess('Status saved successfully!');
        apiClient.get(`/ConsignmentStatusHistory.php?consignmentid=${consignmentData.ConsignmentID}`)
          .then(r => setStatusHistory(r.data.success ? r.data.data || [] : [])).catch(() => {});
        setForm(p => ({ StatusDate: new Date().toISOString().slice(0, 16), CourierStatusID: '', Location: p.Location, Remarks: '', EstimatedDeliveryDate: '', EstimatedDeliveryTimeSlot: '', ActualDeliveryDate: '', ActualDeliveryTime: '', DeliveryOTP: '', DeliveredByID: '', DeliveredBy: '', ReceivedBy: '', DeliveryStatus: '', UndeliveredReason: '', NextAttemptDateTime: '', SpecialTimeDeliveryCharge: '', AfterHoursDeliveryCharge: '' }));
        setSigPreview(null); setPhotoPreview(null);
        setTimeout(() => setSuccess(''), 3000);
      } else { setError(res.data.message || 'Save failed'); }
    } catch (err) { setError('Error: ' + (err.response?.data?.message || err.message)); }
    finally { setSaving(false); }
  };

  const handleClear = () => {
    setConsignmentData(null); setStatusHistory([]);
    setForm({ StatusDate: new Date().toISOString().slice(0, 16), CourierStatusID: '', Location: '', Remarks: '', EstimatedDeliveryDate: '', EstimatedDeliveryTimeSlot: '', ActualDeliveryDate: '', ActualDeliveryTime: '', DeliveryOTP: '', DeliveredByID: '', DeliveredBy: '', ReceivedBy: '', DeliveryStatus: '', UndeliveredReason: '', NextAttemptDateTime: '', SpecialTimeDeliveryCharge: '', AfterHoursDeliveryCharge: '' });
    setSigPreview(null); setPhotoPreview(null); setError(''); setSuccess('');
  };

  const showUndelivered = form.DeliveryStatus && form.DeliveryStatus.toLowerCase() !== 'delivered';

  const inpCls  = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm bg-white outline-none transition-all duration-150 focus:border-blue-400 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] font-[inherit] text-gray-900 box-border';
  const selCls  = inpCls + ' cursor-pointer';
  const lblCls  = 'block mb-1 text-[0.76rem] font-bold text-gray-700 uppercase tracking-wide';
  const secCls  = 'bg-white border border-gray-200 rounded-xl p-5 mb-4';
  const gridCls = 'grid gap-4';

  const SecHdr = ({ bg, color, icon: Icon, children }) => (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3.5 text-[0.87rem] font-bold"
      style={{ background: bg, color }}>
      {Icon && <Icon size={14} />}{children}
    </div>
  );

  const spinStyle = { width: 15, height: 15, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'cse-spin 0.7s linear infinite' };

  return (
    <div className="px-6 pb-16 pt-5 box-border">
      <style>{`@keyframes cse-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Toolbar */}
      <div className="flex gap-2.5 flex-wrap mb-5">
        <button onClick={handleSave} disabled={saving || !consignmentData}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-500 border-0 rounded-lg cursor-pointer hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150">
          {saving ? <><span style={spinStyle} />Saving…</> : <><Save size={15} />Save Status (F5)</>}
        </button>
        <button onClick={handleClear}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gray-500 border-0 rounded-lg cursor-pointer hover:brightness-110 transition-all duration-150">
          <RotateCcw size={15} />Clear
        </button>
        <button onClick={() => setShowHelp(true)} title="How to use this page"
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-blue-500 border-0 rounded-lg cursor-pointer hover:brightness-110 transition-all duration-150">
          <HelpCircle size={15} />Help
        </button>
      </div>

      <div className="max-w-[1080px] mx-auto">

        {error   && <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-300 text-red-700 rounded-lg mb-3.5 text-sm"><AlertCircle size={17} /><strong>Error:</strong> {error}</div>}
        {success && <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-lg mb-3.5 text-sm"><CheckCircle size={17} /><strong>Success:</strong> {success}</div>}

        {/* Search */}
        <div className={secCls}>
          <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-800 mb-3.5 pb-3 border-b-2 border-gray-100">
            <Search size={17} className="text-blue-500" /> Search Consignment
          </div>
          {consignmentData ? (
            <SelectedBar data={consignmentData} onClear={handleClear} />
          ) : (
            <>
              <ConsignmentSearchBox onSelect={handleSelect} />
              <p className="mt-2 text-[0.77rem] text-gray-400">Type consignment number, shipper, or consignee — results appear instantly. Use ↑↓ &amp; Enter to select.</p>
            </>
          )}
        </div>

        {/* Consignment Details */}
        {consignmentData && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4">
            <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-blue-700 mb-3.5 pb-3 border-b border-blue-200">
              <Package size={17} /> Consignment Details
            </div>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))' }}>
              {[
                { l: 'Consignment No', v: consignmentData.ConsignmentNo, mono: true },
                { l: 'Date', v: new Date(consignmentData.ConsignmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
                { l: 'Shipper',   v: consignmentData.ShipperName   || consignmentData.Shipper   || '-' },
                { l: 'Consignee', v: consignmentData.ConsigneeName || consignmentData.Consignee || '-' },
                { l: 'Route',  v: `${consignmentData.OriginCity || 'N/A'} → ${consignmentData.DestinationCity || 'N/A'}` },
                { l: 'Weight', v: `${consignmentData.Weight || 0} kg` },
                { l: 'Pieces', v: consignmentData.Pieces || 0 },
              ].map(x => (
                <div key={x.l}>
                  <div className="text-[0.69rem] text-blue-600 font-bold uppercase tracking-wide mb-0.5">{x.l}</div>
                  <div className={`font-semibold text-blue-900 text-[0.87rem] ${x.mono ? 'font-mono' : ''}`}>{x.v}</div>
                </div>
              ))}
              <div>
                <div className="text-[0.69rem] text-blue-600 font-bold uppercase tracking-wide mb-0.5">Type</div>
                <TypeBadge type={consignmentData.Type} />
              </div>
            </div>
          </div>
        )}

        {consignmentData && (<>

          {/* Core Status */}
          <div className={secCls}>
            <SecHdr bg="#dbeafe" color="#1e3a5f" icon={Navigation}>Core Status Update</SecHdr>
            <div className={`${gridCls} mb-0`} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Status Date &amp; Time *</label><input type="datetime-local" name="StatusDate" value={form.StatusDate} onChange={handleChange} className={inpCls} /></div>
              <div><label className={lblCls}>Courier Status *</label>
                <select name="CourierStatusID" value={form.CourierStatusID} onChange={handleChange} className={selCls}>
                  <option value="">-- Select Status --</option>
                  {courierStatuses.map(s => <option key={s.CourierStatusID} value={s.CourierStatusID}>{s.StatusName}</option>)}
                </select>
              </div>
              <div><label className={lblCls}>Location</label><input type="text" name="Location" value={form.Location} onChange={handleChange} placeholder="Current location" className={inpCls} /></div>
              <div className="col-span-full" style={{ gridColumn: '1/-1' }}>
                <label className={lblCls}>Remarks</label>
                <textarea name="Remarks" value={form.Remarks} onChange={handleChange} rows={2} placeholder="Additional notes…" className={`${inpCls} resize-y min-h-[65px]`} />
              </div>
            </div>
          </div>

          {/* Section 1 — Delivery Date & Time */}
          <div className={secCls}>
            <SecHdr bg="#fef3c7" color="#78350f" icon={Calendar}>1. Delivery Date &amp; Time Details</SecHdr>
            <div className={gridCls} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Estimated Delivery Date</label><input type="date" name="EstimatedDeliveryDate" value={form.EstimatedDeliveryDate} onChange={handleChange} className={inpCls} /></div>
              <div><label className={lblCls}>Estimated Time Slot</label>
                <select name="EstimatedDeliveryTimeSlot" value={form.EstimatedDeliveryTimeSlot} onChange={handleChange} className={selCls}>
                  <option value="">-- Select Slot --</option>
                  <option value="Morning (9 AM – 12 PM)">Morning (9 AM – 12 PM)</option>
                  <option value="Afternoon (12 PM – 4 PM)">Afternoon (12 PM – 4 PM)</option>
                  <option value="Evening (4 PM – 8 PM)">Evening (4 PM – 8 PM)</option>
                  <option value="Night Delivery">Night Delivery</option>
                </select>
              </div>
              <div><label className={lblCls}>Actual Delivery Date</label><input type="date" name="ActualDeliveryDate" value={form.ActualDeliveryDate} onChange={handleChange} className={inpCls} /></div>
              <div><label className={lblCls}>Actual Delivery Time</label><input type="time" name="ActualDeliveryTime" value={form.ActualDeliveryTime} onChange={handleChange} className={inpCls} /></div>
            </div>
          </div>

          {/* Section 2 — Delivery Confirmation */}
          <div className={secCls}>
            <SecHdr bg="#d1fae5" color="#064e3b" icon={CheckCircle}>2. Delivery Confirmation</SecHdr>
            <div className={gridCls} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Delivery OTP</label><input type="text" name="DeliveryOTP" value={form.DeliveryOTP} onChange={handleChange} placeholder="6-digit OTP" maxLength={10} className={inpCls} /></div>
              <div>
                <label className={lblCls}>Delivered By (Employee)</label>
                <select name="DeliveredByID" value={form.DeliveredByID} onChange={handleChange} className={selCls}>
                  <option value="">-- Select Employee --</option>
                  {employees.map(emp => <option key={emp.employeeid} value={emp.employeeid}>{emp.employeename}{emp.employeecode ? ` (${emp.employeecode})` : ''}</option>)}
                </select>
                {employees.length === 0 && <span className="block text-[0.73rem] text-red-500 mt-0.5">No active employees found</span>}
              </div>
              <div><label className={lblCls}>Delivered By (Name)</label><input type="text" name="DeliveredBy" value={form.DeliveredBy} onChange={handleChange} placeholder="Staff name or ID" className={inpCls} /></div>
              <div><label className={lblCls}>Received By</label><input type="text" name="ReceivedBy" value={form.ReceivedBy} onChange={handleChange} placeholder="Recipient name" className={inpCls} /></div>

              {/* Signature upload */}
              <div>
                <label className={lblCls}>Signature (POD)</label>
                <div className="border border-dashed border-gray-400 rounded-lg p-3 text-center bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-100"
                  onClick={() => document.getElementById('sigInput').click()}>
                  {sigPreview
                    ? <div className="relative inline-block">
                        <img src={sigPreview} alt="Sig" className="max-h-[60px] max-w-full rounded" />
                        <button type="button" onClick={e => { e.stopPropagation(); setSigPreview(null); }}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 border-0 rounded-full text-white cursor-pointer flex items-center justify-center text-sm">×</button>
                      </div>
                    : <div className="text-gray-400 text-[0.83rem]"><FileText size={20} className="mx-auto mb-1 opacity-40" />Click to upload</div>}
                </div>
                <input id="sigInput" type="file" accept="image/*" className="hidden" onChange={e => handleFile(e, 'sig')} />
              </div>

              {/* Photo upload */}
              <div>
                <label className={lblCls}>Delivery Photo (POD)</label>
                <div className="border border-dashed border-gray-400 rounded-lg p-3 text-center bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-100"
                  onClick={() => document.getElementById('photoInput').click()}>
                  {photoPreview
                    ? <div className="relative inline-block">
                        <img src={photoPreview} alt="Photo" className="max-h-[60px] max-w-full rounded" />
                        <button type="button" onClick={e => { e.stopPropagation(); setPhotoPreview(null); }}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 border-0 rounded-full text-white cursor-pointer flex items-center justify-center text-sm">×</button>
                      </div>
                    : <div className="text-gray-400 text-[0.83rem]"><Camera size={20} className="mx-auto mb-1 opacity-40" />Click to upload</div>}
                </div>
                <input id="photoInput" type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFile(e, 'photo')} />
              </div>
            </div>
          </div>

          {/* Section 3 — Delivery Status */}
          <div className={secCls}>
            <SecHdr bg="#ede9fe" color="#4c1d95" icon={Truck}>3. Delivery Status</SecHdr>
            <div className={gridCls} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Delivery Status</label>
                <select name="DeliveryStatus" value={form.DeliveryStatus} onChange={handleChange} className={selCls}>
                  <option value="">-- Select --</option>
                  {deliveryStatuses.map(s => <option key={s.statusid} value={s.statuscode}>{s.statusname}</option>)}
                </select>
              </div>
              {showUndelivered && (
                <div><label className={lblCls}>Reason (Not Delivered)</label>
                  <select name="UndeliveredReason" value={form.UndeliveredReason} onChange={handleChange} className={selCls}>
                    <option value="">-- Select Reason --</option>
                    {undeliveredReasons.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
              {showUndelivered && (
                <div><label className={lblCls}>Next Attempt Date &amp; Time</label>
                  <input type="datetime-local" name="NextAttemptDateTime" value={form.NextAttemptDateTime} onChange={handleChange} className={inpCls} />
                </div>
              )}
            </div>
            {form.DeliveryStatus && (
              <div className="mt-3 flex items-center gap-2.5 flex-wrap">
                <span className="text-[0.8rem] text-gray-500">Selected:</span>
                <span className={`px-3 py-1 rounded-full text-[0.82rem] font-bold ${deliveryStatusCls(form.DeliveryStatus)}`}>
                  {deliveryStatuses.find(s => s.statuscode === form.DeliveryStatus)?.statusname || form.DeliveryStatus}
                </span>
                {form.UndeliveredReason && <span className="text-[0.83rem] text-gray-500">— {form.UndeliveredReason}</span>}
              </div>
            )}
          </div>

          {/* Section 4 — Delivery Charges */}
          <div className={secCls}>
            <SecHdr bg="#fce7f3" color="#831843" icon={DollarSign}>4. Delivery Charges (Optional)</SecHdr>
            <div className={gridCls} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div>
                <label className={lblCls}>Special Time Delivery Charge</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                  <input type="number" name="SpecialTimeDeliveryCharge" value={form.SpecialTimeDeliveryCharge} onChange={handleChange} placeholder="0.00" min="0" step="0.01" className={`${inpCls} pl-7`} />
                </div>
                <span className="block text-[0.73rem] text-gray-400 mt-0.5">Morning / evening / time-specific</span>
              </div>
              <div>
                <label className={lblCls}>After-Hours Delivery Charge</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                  <input type="number" name="AfterHoursDeliveryCharge" value={form.AfterHoursDeliveryCharge} onChange={handleChange} placeholder="0.00" min="0" step="0.01" className={`${inpCls} pl-7`} />
                </div>
                <span className="block text-[0.73rem] text-gray-400 mt-0.5">Night / outside business hours</span>
              </div>
              {(form.SpecialTimeDeliveryCharge || form.AfterHoursDeliveryCharge) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3.5">
                  <div className="text-[0.76rem] font-bold text-gray-500 mb-2 uppercase tracking-wide">Charges Summary</div>
                  {form.SpecialTimeDeliveryCharge && <div className="flex justify-between text-[0.86rem] mb-1"><span>Special Time:</span><strong>₹{parseFloat(form.SpecialTimeDeliveryCharge || 0).toFixed(2)}</strong></div>}
                  {form.AfterHoursDeliveryCharge  && <div className="flex justify-between text-[0.86rem] mb-1"><span>After-Hours:</span><strong>₹{parseFloat(form.AfterHoursDeliveryCharge || 0).toFixed(2)}</strong></div>}
                  <div className="flex justify-between font-bold text-[0.92rem] pt-1.5 border-t border-gray-200 mt-1.5">
                    <span>Total:</span>
                    <span className="text-blue-700">₹{(parseFloat(form.SpecialTimeDeliveryCharge || 0) + parseFloat(form.AfterHoursDeliveryCharge || 0)).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status History */}
          <div className={secCls}>
            <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-800 mb-3.5 pb-3 border-b-2 border-gray-100">
              <Clock size={17} className="text-blue-500" />
              Status History ({statusHistory.length} {statusHistory.length === 1 ? 'update' : 'updates'})
            </div>
            {statusHistory.length === 0 ? (
              <div className="py-7 text-center bg-gray-50 rounded-lg text-gray-400">
                <Clock size={36} className="mx-auto mb-2 opacity-20" />
                <div className="font-semibold text-[0.9rem]">No status updates yet</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[0.84rem]">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      {['#','Date & Time','Status','Delivery Status','Location','Est. Delivery','Received By','Charges','By'].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left text-[0.73rem] font-bold uppercase tracking-wide text-gray-600 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {statusHistory.map((s, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-75">
                        <td className="px-3 py-2.5 text-center font-semibold text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="flex items-center gap-1"><Calendar size={12} />{new Date(s.StatusDate).toLocaleString()}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-[0.78rem] font-semibold">{s.StatusName || s.Status}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          {s.DeliveryStatus
                            ? <span className={`px-2 py-0.5 rounded text-[0.78rem] font-semibold ${deliveryStatusCls(s.DeliveryStatus)}`}>{s.DeliveryStatus}</span>
                            : '-'}
                        </td>
                        <td className="px-3 py-2.5"><span className="flex items-center gap-1"><MapPin size={12} />{s.Location || '-'}</span></td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {s.EstimatedDeliveryDate ? new Date(s.EstimatedDeliveryDate).toLocaleDateString() : '-'}
                          {s.EstimatedDeliveryTimeSlot ? ` (${s.EstimatedDeliveryTimeSlot.split('(')[0].trim()})` : ''}
                        </td>
                        <td className="px-3 py-2.5">{s.ReceivedBy || '-'}</td>
                        <td className="px-3 py-2.5">
                          {(s.SpecialTimeDeliveryCharge || s.AfterHoursDeliveryCharge)
                            ? `₹${(parseFloat(s.SpecialTimeDeliveryCharge || 0) + parseFloat(s.AfterHoursDeliveryCharge || 0)).toFixed(2)}`
                            : '-'}
                        </td>
                        <td className="px-3 py-2.5">{s.UpdatedByName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Bottom save button */}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={handleSave} disabled={saving || !consignmentData}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-emerald-500 border-0 rounded-lg cursor-pointer hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150">
              {saving ? <><span style={spinStyle} />Saving…</> : <><Save size={15} />Save Status Update</>}
            </button>
          </div>

        </>)}
      </div>

      {showHelp && (
        <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl w-full max-w-[640px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0" style={{ background: '#3b82f6' }}>
              <h3 className="flex items-center gap-2 text-lg font-semibold m-0 text-white">
                <HelpCircle size={18} /> How to Use This Page
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150 text-white">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {[
                { title: 'Find a Consignment', desc: 'Type a consignment number, shipper, or consignee name in the search box. Use ↑↓ to navigate results and Enter to select.' },
                { title: 'Core Status Update', desc: 'Set the Status Date/Time and Courier Status (required), plus the current Location and any Remarks.' },
                { title: 'Delivery Date & Time', desc: 'Optionally set an Estimated Delivery Date/Time Slot, and Actual Delivery Date/Time once it happens.' },
                { title: 'Delivery Confirmation', desc: 'Record a Delivery OTP if used, who delivered it, who received it, and optionally attach a signature or delivery photo as proof.' },
                { title: 'Delivery Status', desc: 'Mark whether it was Delivered or not. If not delivered, a Reason and Next Attempt Date/Time appear.' },
                { title: 'Delivery Charges', desc: 'Add optional charges for special-time or after-hours delivery — a running total is shown.' },
                { title: 'Save', desc: 'Click "Save Status" (or press F5) to record this update. It appears immediately in the Status History table below.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: '#3b82f6' }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer" style={{ background: '#3b82f6' }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsignmentStatusEntry;